import {
  AspectRatio,
  Box,
  Flex,
  Tag,
  Td,
  Text,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { LabelPromo } from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  LIST_FEATURE_FLAGS,
  numberWithSeparator,
  useFeatureFlag,
} from '@diamond/shared/utils';
import {
  getOrderDetailProductDisplay,
  mapStockConfirmationStatusToUi,
  ProductWithPromo,
  readStockConfirmationStatus,
} from '@diamond/sol-buyer/utils';
import { OrderDetailPromoPrice } from '@diamond/sol-buyer-components';

import { CheckIconVoucher } from '../../../carts/Icons';
import { CheckboxOutlineBlankIcon } from '../../../checkout-new/icons';
import { EstimatedWeightOrderDetailStatus } from '../../../estimated-weight-product';
import { Image } from '../../image';

const isValidNote = (note: unknown): note is string => {
  if (!note || typeof note !== 'string') return false;
  const trimmed = note.trim();
  return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined';
};

type ProductRowProps = {
  product: ProductWithPromo;
  isVoucherMenuEnabled: boolean;
  poDetailsDataEnabled?: boolean;
  divIdx: number;
  productIdx: number;
};

export const ProductRow = ({
  product,
  isVoucherMenuEnabled,
  poDetailsDataEnabled = false,
  divIdx,
  productIdx,
}: ProductRowProps) => {
  const isEstimatedWeightEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_ESTIMATED_WEIGHT_PRODUCT,
    true
  );
  const estimatedWeightStatus = mapStockConfirmationStatusToUi(
    readStockConfirmationStatus(product),
    isEstimatedWeightEnabled
  );
  const display = getOrderDetailProductDisplay(product, poDetailsDataEnabled);
  return (
    <Tr key={`product-row-${divIdx}-${productIdx}`}>
      <Td pt="0">
        <Flex w="full" gap="2">
          <Flex
            key={`product-image-${divIdx}-${productIdx}`}
            justifyContent="center"
            alignItems="center"
            rounded="lg"
            border="1px solid #F2F2F7"
            p="1"
          >
            <AspectRatio
              w="80px"
              ratio={1 / 1}
              flex="none"
              opacity={product.is_not_processed ? 0.7 : 1}
            >
              <Image
                fill
                src={product.product_images_medium?.[0]}
                alt={product.desc ?? ''}
                style={{
                  backgroundColor: 'white',
                  objectFit: 'contain',
                }}
              />
            </AspectRatio>
          </Flex>
          <Flex
            flexDir="column"
            justifyContent="start"
            alignItems="start"
            gap="1"
          >
            <Flex flexDir="row" gap="2" alignItems="center">
              <Text
                fontSize="xs"
                fontWeight="bold"
                textColor={product.is_not_processed ? 'gray' : 'black'}
              >
                {product.brand_desc}
              </Text>
              {isVoucherMenuEnabled &&
                (product.voucher_free_goods?.length ?? 0) > 0 && (
                  <CheckIconVoucher boxSize="16px" flexShrink={0} />
                )}
              {(product.free_good_data !== null ||
                (product.voucher_free_goods?.length ?? 0) > 0) && (
                <LabelPromo type="free-item" />
              )}
              {product.hasCashback && <LabelPromo type="cashback" />}
              {product.is_not_processed ? (
                <Tag
                  ml={2}
                  borderRadius="full"
                  background="gray"
                  color="white"
                  fontWeight="bold"
                >
                  Dibatalkan
                </Tag>
              ) : null}
            </Flex>
            <Text
              fontSize="xs"
              textColor={product.is_not_processed ? 'gray' : 'black'}
            >
              {product.desc}
            </Text>
            {estimatedWeightStatus ? (
              <EstimatedWeightOrderDetailStatus status={estimatedWeightStatus} />
            ) : null}
            {isValidNote(product.note) && (
              <Flex
                gap={2}
                align="start"
                px={0}
                py={1}
                bg="orange.50"
                borderRadius="md"
                alignItems="center"
                opacity={product.is_not_processed ? 0.7 : 1}
                maxW="220px"
              >
                <CheckboxOutlineBlankIcon
                  sx={{
                    fontSize: '20px',
                    color: '#8E8E93',
                  }}
                />
                {product.note.length > 30 ? (
                  <Tooltip label={product.note} hasArrow shouldWrapChildren>
                    <Text fontSize="xs" color="orange.800" noOfLines={1}>
                      {product.note}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text fontSize="xs" color="orange.800">
                    {product.note}
                  </Text>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Td>
      <Td pt="0">
        <Flex
          key={`product-qty-${divIdx}-${productIdx}`}
          minH="80px"
          w="100%"
          alignItems="center"
        >
          <Box textColor={product.is_not_processed ? 'gray' : 'black'}>
            <Text fontSize="md">
              {numberWithSeparator(display.confirmedQuantity)}
            </Text>
            {display.showInitialQuantity ? (
              <Box color="gray" mt={2}>
                <Text fontSize="sm">Pesanan Awal</Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {numberWithSeparator(display.quantity)}
                </Text>
              </Box>
            ) : null}
          </Box>
        </Flex>
      </Td>
      <Td pt="0">
        {display.showPriceSection ? (
          <Flex
            key={`product-price-${divIdx}-${productIdx}`}
            minH="80px"
            w="100%"
            alignItems="center"
          >
            <Box>
              <Box mb="1">
                <OrderDetailPromoPrice
                  product={product}
                  isVoucherMenuEnabled={isVoucherMenuEnabled}
                  originalUnitPrice={display.originalUnitPrice}
                />
              </Box>
              <Text
                fontSize="md"
                textColor={product.is_not_processed ? 'gray' : 'black'}
              >
                {buyerCurrencyFormatter(display.unitPrice)}
              </Text>
              {display.showInitialUnitPrice ? (
                <Box color="gray" mt={2}>
                  <Text fontSize="sm">harga awal</Text>
                  <Text fontSize="sm" fontWeight="semibold">
                    {buyerCurrencyFormatter(display.initialUnitPrice)}
                  </Text>
                </Box>
              ) : null}
            </Box>
          </Flex>
        ) : null}
      </Td>
      <Td pt="0">
        {display.showPriceSection ? (
          <Flex
            key={`product-sub-total-${divIdx}-${productIdx}`}
            minH="80px"
            w="100%"
            alignItems="center"
            color={product.is_not_processed ? 'gray' : 'black'}
          >
            <Box>
              <Text fontSize="md">
                {buyerCurrencyFormatter(display.totalPrice)}
              </Text>
              {display.showInitialTotalPrice ? (
                <Box color="gray" mt={2}>
                  <Text fontSize="sm">total awal</Text>
                  <Text fontSize="sm" fontWeight="semibold">
                    {buyerCurrencyFormatter(display.initialTotalPrice)}
                  </Text>
                </Box>
              ) : null}
            </Box>
          </Flex>
        ) : null}
      </Td>
    </Tr>
  );
};
